import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, Image} from 'react-native';
import {
  Text, TextInput, Button, Appbar, ActivityIndicator, Snackbar,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useMeasurementStore} from '../../store/useMeasurementStore';
import {useConfigStore} from '../../store/useConfigStore';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import {colors, spacing} from '../../theme';

const BodyMeasurementScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {customerId, customerGender} = route.params;

  const {
    measurementTypes, bodyProfile, isLoading,
    fetchMeasurementTypes, fetchBodyProfile, saveBodyProfile,
  } = useMeasurementStore();
  const {fetchByType, getProperty} = useConfigStore();
  const {orgUnits, selectedOrgUnitId, fetchOrgUnits} = useOrgUnitStore();

  const [values, setValues] = useState({});
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  const effectiveOrgUnitId = orgUnits.length === 1 ? orgUnits[0].id : selectedOrgUnitId;

  useEffect(() => {
    if (orgUnits.length === 0) fetchOrgUnits();
  }, [orgUnits.length, fetchOrgUnits]);

  useEffect(() => {
    if (effectiveOrgUnitId) fetchByType('ORDER', effectiveOrgUnitId);
  }, [effectiveOrgUnitId, fetchByType]);

  const measurementUnit = getProperty('ORDER', 'MEASUREMENT_UNIT', effectiveOrgUnitId) || 'INCHES';
  const unitLabel = measurementUnit === 'CMS' ? 'cms' : 'in';

  useEffect(() => {
    const genderFilter = customerGender === 'MALE' || customerGender === 'FEMALE'
      ? customerGender : null;
    fetchMeasurementTypes(genderFilter);
    fetchBodyProfile(customerId);
  }, [customerId, customerGender, fetchMeasurementTypes, fetchBodyProfile]);

  // Pre-fill values when body profile loads
  useEffect(() => {
    if (bodyProfile?.values) {
      const prefilled = {};
      bodyProfile.values.forEach(v => {
        prefilled[v.globalOptionId] = v.value;
      });
      setValues(prefilled);
      if (bodyProfile.notes) {
        setNotes(bodyProfile.notes);
      }
    }
  }, [bodyProfile]);

  // Sort: BOTH first, then gender-specific
  const sortedTypes = [...measurementTypes].sort((a, b) =>
    a.gender === 'BOTH' && b.gender !== 'BOTH' ? -1 : a.gender !== 'BOTH' && b.gender === 'BOTH' ? 1 : 0,
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const measurementValues = Object.entries(values)
        .filter(([_, v]) => v && v.toString().trim() !== '')
        .map(([typeId, value]) => ({
          globalOptionId: Number(typeId),
          value: value.toString().trim(),
        }));

      await saveBodyProfile(customerId, {
        measuredAt: new Date().toISOString().split('T')[0],
        notes: notes.trim() || null,
        values: measurementValues,
      });
      navigation.goBack();
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  }, [values, notes, customerId, saveBodyProfile, navigation, t]);

  if (isLoading && measurementTypes.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  const renderTypeInputs = (types, startIndex) => {
    if (types.length === 0) return null;
    return (
      <>
        {types.map((mt, idx) => {
          const imgUrl = customerGender === 'MALE' ? mt.imageUrl1 : mt.imageUrl2;
          const serialNo = startIndex + idx + 1;
          return (
            <View key={mt.id} style={styles.measurementRow}>
              <Text style={styles.serialNo}>{serialNo}</Text>
              {imgUrl ? (
                <Image source={{uri: imgUrl}} style={styles.measurementImage} />
              ) : (
                <View style={styles.measurementImageEmpty} />
              )}
              <TextInput
                label={`${mt.name} (${unitLabel})`}
                value={values[mt.id]?.toString() || ''}
                onChangeText={v => setValues(prev => ({...prev, [mt.id]: v}))}
                mode="outlined"
                style={styles.measurementInput}
                keyboardType="numeric"
                outlineColor={colors.border}
                activeOutlineColor={colors.primary}
              />
            </View>
          );
        })}
      </>
    );
  };

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content
          title={t('measurement.bodyMeasurements')}
          titleStyle={styles.appbarTitle}
        />
      </Appbar.Header>

      <ScrollView style={styles.content} keyboardShouldPersistTaps="handled">
        <View style={styles.unitBanner}>
          <Text style={styles.unitBannerText}>All measurements in {measurementUnit === 'CMS' ? 'Centimeters (cms)' : 'Inches (in)'}</Text>
        </View>
        {renderTypeInputs(sortedTypes, 0)}

        <TextInput
          label={t('measurement.notes')}
          value={notes}
          onChangeText={setNotes}
          mode="outlined"
          style={styles.input}
          multiline
          numberOfLines={3}
          outlineColor={colors.border}
          activeOutlineColor={colors.primary}
        />

        <Button
          mode="contained"
          onPress={handleSave}
          loading={saving}
          disabled={saving}
          style={styles.saveButton}
          buttonColor={colors.primary}>
          {t('common.save')}
        </Button>
      </ScrollView>

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  content: {flex: 1, padding: spacing.md},
  unitBanner: {
    backgroundColor: colors.primary + '12', borderRadius: 6,
    paddingVertical: spacing.xs, paddingHorizontal: spacing.sm, marginBottom: spacing.sm,
  },
  unitBannerText: {fontSize: 13, fontWeight: '600', color: colors.primary, textAlign: 'center'},
  sectionHeader: {
    fontWeight: '600', color: colors.textSecondary,
    marginTop: spacing.md, marginBottom: spacing.sm,
  },
  input: {marginBottom: spacing.sm, backgroundColor: colors.surface},
  serialNo: {
    width: 24,
    alignSelf: 'center',
    textAlign: 'center',
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
  },
  measurementRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.sm,
    gap: spacing.sm,
  },
  measurementImage: {
    width: 100,
    height: 100,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#CCC',
    backgroundColor: colors.background,
  },
  measurementImageEmpty: {
    width: 100,
    height: 100,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#CCC',
    backgroundColor: colors.background,
  },
  measurementInput: {
    flex: 1,
    backgroundColor: colors.surface,
    alignSelf: 'center',
  },
  saveButton: {marginTop: spacing.md, marginBottom: spacing.xxl, borderRadius: 8},
});

export default BodyMeasurementScreen;
